'use client'

/**
 * BIRHAUS Provider
 *
 * Centralized context provider for BIRHAUS configuration, real-time validation,
 * and analytics tracking. This component wraps your entire application to:
 *
 * - Enforce cognitive load limits globally
 * - Track UX violations in real-time
 * - Provide centralized theme and language management
 * - Send analytics data for UX improvements
 * - Enable performance budget monitoring
 */

import * as React from 'react'
import { 
  BirhausScore,
  BIRHAUS_ENV_CONFIG
} from '@birhaus/primitives'
import { UndoProvider } from '@birhaus/core'
import { 
  borderRadiusV3,
  spacingV3,
  glassV3,
  animationsV3,
  typographyV3
} from '@birhaus/tokens/v3'

/**
 * ErrorBoundary for BIRHAUS components
 * Provides graceful error handling with Spanish-first error messages
 */
interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

interface BirhausErrorBoundaryProps {
  children: React.ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  fallback?: React.ReactNode
  language?: 'es' | 'en'
}

class BirhausErrorBoundary extends React.Component<BirhausErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: BirhausErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo })
    this.props.onError?.(error, errorInfo)
    
    // Log in development
    const isDev = typeof process !== 'undefined' && process.env.NODE_ENV === 'development'
    if (isDev) {
      console.group('🚨 BIRHAUS Error Boundary')
      console.error('Error:', error)
      console.error('Error Info:', errorInfo)
      console.groupEnd()
    }
  }

  render() {
    if (this.state.hasError) {
      const language = this.props.language || 'es'
      const errorMessage = language === 'es' 
        ? 'Algo salió mal. Por favor, recarga la página.'
        : 'Something went wrong. Please reload the page.'

      return this.props.fallback || (
        <div 
          className="birhaus-error-boundary"
          style={{
            padding: '24px',
            margin: '16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#b91c1c'
          }}
        >
          <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
            {language === 'es' ? '⚠️ Error BIRHAUS' : '⚠️ BIRHAUS Error'}
          </h2>
          <p style={{ margin: '0', fontSize: '14px' }}>
            {errorMessage}
          </p>
          {typeof process !== 'undefined' && process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: '12px', fontSize: '12px' }}>
              <summary>Detalles técnicos</summary>
              <pre style={{ 
                marginTop: '8px', 
                padding: '8px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
                {this.state.error.toString()}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}

interface BirhausConfig {
  /** Strict mode enforcement (throws on violations in dev) */
  strictMode?: boolean
  /** Track cognitive load violations in real-time */
  cognitiveLoadTracking?: boolean
  /** Monitor performance budgets */
  performanceBudgets?: boolean
  /** Validate accessibility in real-time */
  accessibilityValidation?: boolean
  /** Enforce Spanish-first design */
  spanishFirst?: boolean
  /** Enforce undo-over-confirm pattern */
  undoOverConfirm?: boolean
  /** Enforce Miller's Law limits */
  millerLawEnforcement?: boolean
  /** Enable progressive disclosure suggestions */
  progressiveDisclosure?: boolean
  /** Show development warnings and hints */
  showWarnings?: boolean
  /** Detailed logging for debugging */
  detailedLogging?: boolean
  /** Enable analytics collection */
  analyticsEnabled?: boolean
  /** v3.0 Radical Minimalist Design Features */
  v3?: {
    /** Enable glass morphism effects globally */
    glassEnabled?: boolean
    /** Glass morphism intensity (0-100) */
    glassIntensity?: number
    /** Enable radical border radius (28px+) */
    radicalBorders?: boolean
    /** Enable generous spacing (2x traditional) */
    generousSpacing?: boolean
    /** Enable smooth GPU-accelerated animations */
    smoothAnimations?: boolean
    /** Prefer reduced motion (accessibility) */
    reducedMotion?: boolean
    /** Enable floating UI elements */
    floatingElements?: boolean
    /** Enable magnetic hover effects */
    magneticHover?: boolean
    /** v3 feature gradual rollout flags */
    enableNewComponents?: boolean
    /** Performance-first animation mode */
    performanceMode?: boolean
  }
}

interface BirhausProviderProps {
  /** Primary language (defaults to Spanish-first) */
  language?: 'es' | 'en'
  /** Configuration options */
  config?: BirhausConfig
  /** Custom theme object */
  theme?: Record<string, unknown>
  /** Analytics endpoint for UX data */
  analyticsEndpoint?: string
  /** Custom score thresholds */
  scoreThresholds?: {
    warning: number    // Default: 70
    critical: number   // Default: 50
  }
  /** Development mode features */
  devMode?: boolean
  /** Child components */
  children: React.ReactNode
  /** Called when violations are detected */
  onViolation?: (violation: BirhausViolation) => void
  /** Called when score changes */
  onScoreChange?: (score: BirhausScore) => void
  /** UndoProvider configuration */
  undoConfig?: {
    /** Default timeout for undo actions (ms) */
    defaultTimeoutMs?: number
    /** Maximum concurrent undo actions */
    maxActions?: number
  }
  /** Disable UndoProvider integration (for custom provider setup) */
  disableUndo?: boolean
  /** Error boundary configuration */
  errorBoundary?: {
    /** Custom error fallback component */
    fallback?: React.ReactNode
    /** Called when an error is caught */
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  }
  /** Disable ErrorBoundary integration */
  disableErrorBoundary?: boolean
  /** Sub-providers configuration */
  subProviders?: BirhausSubProvidersConfig
}

interface BirhausViolation {
  type: 'cognitive' | 'accessibility' | 'spanish' | 'performance' | 'confirmation'
  severity: 'warning' | 'error' | 'critical'
  element: Element | null
  message: string
  messageEs: string
  messageEn: string
  recommendation: string
  birhausPrinciple: number
  timestamp: Date
}

// Sub-provider types
export type LocaleSupported = 'es-PY' | 'es-ES' | 'en-US'
export type ComplianceStandard = 'SEPRELAD' | 'PCI' | 'GDPR' | 'SOC2'
export type IndustryTheme = 'financial' | 'educational' | 'healthcare' | 'government'

export interface BirhausLocaleConfig {
  current: LocaleSupported
  fallback: LocaleSupported
  dateFormat: string
  numberFormat: string
  currencyCode: string
}

export interface BirhausComplianceConfig {
  standards: ComplianceStandard[]
  auditTrail: boolean
  dataRetentionDays: number
  encryptionRequired: boolean
}

export interface BirhausThemeConfig {
  industry: IndustryTheme
  cssVariables: Record<string, string>
  darkMode: boolean
  customStyles?: Record<string, unknown>
  /** v3.0 Theme Extensions */
  v3?: {
    /** v3 design tokens integration */
    useV3Tokens?: boolean
    /** Glass morphism theme variant */
    glassTheme?: keyof typeof glassV3.effects
    /** Border radius theme scale */
    borderRadiusScale?: keyof typeof borderRadiusV3
    /** Spacing scale multiplier */
    spacingScale?: number
    /** Animation timing preference */
    animationTiming?: keyof typeof animationsV3.duration
    /** Typography scale preference */
    typographyScale?: keyof typeof typographyV3.fontSize
  }
}

export interface BirhausLoadingConfig {
  globalLoading: boolean
  defaultTimeout: number
  showSkeletons: boolean
  loadingMessages: {
    es: string
    en: string
  }
}

export interface BirhausSubProvidersConfig {
  locale?: BirhausLocaleConfig
  compliance?: BirhausComplianceConfig
  theme?: BirhausThemeConfig
  loading?: BirhausLoadingConfig
}

export interface BirhausContextType {
  /** Current configuration */
  config: BirhausConfig
  /** Current language */
  language: 'es' | 'en'
  /** Current theme */
  theme: Record<string, unknown>
  /** Current Birhaus score */
  score: BirhausScore | null
  /** List of current violations */
  violations: BirhausViolation[]
  /** Translation function */
  t: (key: string, fallback?: string) => string
  /** Report a violation */
  reportViolation: (violation: Omit<BirhausViolation, 'timestamp'>) => void
  /** Update configuration */
  updateConfig: (newConfig: Partial<BirhausConfig>) => void
  /** Change language */
  setLanguage: (language: 'es' | 'en') => void
  /** Force score recalculation */
  recalculateScore: () => Promise<void>
  /** Sub-providers configuration */
  subProviders: BirhausSubProvidersConfig
  /** Update sub-providers */
  updateSubProviders: (newConfig: Partial<BirhausSubProvidersConfig>) => void
  /** Whether we're on the client side (post-hydration) - SSR safe */
  isClient: boolean
  /** v3.0 Design System Utilities */
  v3: {
    /** Get current glass effect settings */
    getGlassEffect: (intensity?: number) => typeof glassV3.effects.light | typeof glassV3.effects.medium | typeof glassV3.effects.heavy
    /** Get radical border radius */
    getRadicalBorders: () => typeof borderRadiusV3
    /** Get generous spacing scale */
    getGenerousSpacing: () => typeof spacingV3
    /** Check if v3 features are enabled */
    isV3Enabled: () => boolean
    /** Get animation preferences with reduced motion respect */
    getAnimationPrefs: () => {
      duration: string
      easing: string
      reducedMotion: boolean
    }
    /** Generate v3 CSS variables for dynamic theming */
    generateV3CSSVars: () => Record<string, string>
    /** Check for browser support of advanced features */
    hasAdvancedSupport: () => {
      backdropFilter: boolean
      transforms: boolean
      animations: boolean
    }
  }
}

// SSR-safe context creation
export const BirhausContext = typeof window !== 'undefined'
  ? React.createContext<BirhausContextType | null>(null)
  : React.createContext<BirhausContextType | null>(null)

/**
 * Runtime validator for real-time DOM monitoring
 */
class BirhausRuntimeValidator {
  private observer: MutationObserver
  private violations: BirhausViolation[] = []
  private onViolation: (violation: BirhausViolation) => void
  private config: BirhausConfig

  constructor(config: BirhausConfig, onViolation: (violation: BirhausViolation) => void) {
    this.config = config
    this.onViolation = onViolation
    this.observer = new MutationObserver(this.handleMutations.bind(this))
  }

  start() {
    // SSR-safe document access
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }
    
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'role', 'aria-label']
    })
    
    // Initial scan
    this.validateElement(document.body)
  }

  stop() {
    this.observer.disconnect()
  }

  private handleMutations(mutations: MutationRecord[]) {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.validateElement(node as Element)
          }
        })
      }
    })
  }

  // eslint-disable-next-line complexity -- Element validation requires multiple BIRHAUS principle checks
  private validateElement(element: Element) {
    if (!this.config.cognitiveLoadTracking) return

    // Miller's Law: Navigation items
    if (element.matches('nav') || element.querySelector('nav')) {
      this.validateNavigation(element)
    }

    // Miller's Law: Form fields
    if (element.matches('form') || element.querySelector('form')) {
      this.validateForms(element)
    }

    // Miller's Law: Select options
    if (element.matches('select') || element.querySelector('select')) {
      this.validateSelects(element)
    }

    // Confirmation dialogs
    if (this.config.undoOverConfirm) {
      this.validateConfirmationDialogs(element)
    }

    // Spanish-first labels
    if (this.config.spanishFirst) {
      this.validateSpanishFirst(element)
    }

    // Accessibility
    if (this.config.accessibilityValidation) {
      this.validateAccessibility(element)
    }
  }

  private validateNavigation(element: Element) {
    const navElements = element.querySelectorAll('nav')
    
    navElements.forEach(nav => {
      const navItems = nav.querySelectorAll('a, button')
      
      if (navItems.length > 7) {
        this.reportViolation({
          type: 'cognitive',
          severity: 'warning',
          element: nav,
          message: `Navigation has ${navItems.length} items (max recommended: 7)`,
          messageEs: `Navegación con ${navItems.length} elementos (máximo recomendado: 7)`,
          messageEn: `Navigation has ${navItems.length} items (max recommended: 7)`,
          recommendation: 'Consider grouping related navigation items into submenus',
          birhausPrinciple: 1 // Form serves flow
        })
      }
    })
  }

  private validateForms(element: Element) {
    const forms = element.querySelectorAll('form')
    
    forms.forEach(form => {
      const visibleFields = form.querySelectorAll('input:not([type="hidden"]), select, textarea')
      
      if (visibleFields.length > 7) {
        this.reportViolation({
          type: 'cognitive',
          severity: 'warning',
          element: form,
          message: `Form has ${visibleFields.length} visible fields (max recommended: 7)`,
          messageEs: `Formulario con ${visibleFields.length} campos visibles (máximo recomendado: 7)`,
          messageEn: `Form has ${visibleFields.length} visible fields (max recommended: 7)`,
          recommendation: 'Use progressive disclosure or split into multiple steps',
          birhausPrinciple: 4 // Progressive disclosure
        })
      }
    })
  }

  private validateSelects(element: Element) {
    const selects = element.querySelectorAll('select')
    
    selects.forEach(select => {
      const options = select.querySelectorAll('option')
      
      if (options.length > 7) {
        this.reportViolation({
          type: 'cognitive',
          severity: 'warning',
          element: select,
          message: `Select has ${options.length} options (max recommended: 7)`,
          messageEs: `Select con ${options.length} opciones (máximo recomendado: 7)`,
          messageEn: `Select has ${options.length} options (max recommended: 7)`,
          recommendation: 'Add search functionality or group options by category',
          birhausPrinciple: 4 // Progressive disclosure
        })
      }
    })
  }

  private validateConfirmationDialogs(element: Element) {
    // Check for confirmation dialogs
    const confirmButtons = element.querySelectorAll('[onclick*="confirm"], [onclick*="alert"]')
    
    confirmButtons.forEach(button => {
      this.reportViolation({
        type: 'confirmation',
        severity: 'error',
        element: button,
        message: 'Confirmation dialog detected - use undo pattern instead',
        messageEs: 'Diálogo de confirmación detectado - usa patrón de deshacer',
        messageEn: 'Confirmation dialog detected - use undo pattern instead',
        recommendation: 'Replace with undo-over-confirm pattern using BirhausButton',
        birhausPrinciple: 5 // Undo over confirm
      })
    })

    // Check for modal dialogs with confirm buttons
    const modals = element.querySelectorAll('[role="dialog"], [role="alertdialog"]')
    modals.forEach(modal => {
      const confirmButtons = modal.querySelectorAll('button[data-confirm], button:contains("confirmar")')
      if (confirmButtons.length > 0) {
        this.reportViolation({
          type: 'confirmation',
          severity: 'error',
          element: modal,
          message: 'Modal confirmation dialog found',
          messageEs: 'Diálogo modal de confirmación encontrado',
          messageEn: 'Modal confirmation dialog found',
          recommendation: 'Use inline undo functionality instead of confirmation modals',
          birhausPrinciple: 5 // Undo over confirm
        })
      }
    })
  }

  private validateSpanishFirst(element: Element) {
    // Check button text
    const buttons = element.querySelectorAll('button')
    buttons.forEach(button => {
      const text = button.textContent?.toLowerCase() || ''
      const englishWords = ['save', 'cancel', 'delete', 'edit', 'view', 'submit', 'close']
      
      if (englishWords.some(word => text.includes(word))) {
        this.reportViolation({
          type: 'spanish',
          severity: 'warning',
          element: button,
          message: `Button with English text: "${button.textContent}"`,
          messageEs: `Botón con texto en inglés: "${button.textContent}"`,
          messageEn: `Button with English text: "${button.textContent}"`,
          recommendation: 'Use Spanish-first labels with BirhausButton component',
          birhausPrinciple: 7 // Bilingual by design
        })
      }
    })

    // Check input placeholders
    const inputs = element.querySelectorAll('input[placeholder], textarea[placeholder]')
    inputs.forEach(input => {
      const placeholder = input.getAttribute('placeholder')?.toLowerCase() || ''
      const englishWords = ['enter', 'type', 'search', 'select', 'choose']
      
      if (englishWords.some(word => placeholder.includes(word))) {
        this.reportViolation({
          type: 'spanish',
          severity: 'warning',
          element: input,
          message: `Input with English placeholder: "${placeholder}"`,
          messageEs: `Input con placeholder en inglés: "${placeholder}"`,
          messageEn: `Input with English placeholder: "${placeholder}"`,
          recommendation: 'Use Spanish-first placeholders with BirhausInput component',
          birhausPrinciple: 7 // Bilingual by design
        })
      }
    })
  }

  private validateAccessibility(element: Element) {
    // Check for missing alt attributes
    const images = element.querySelectorAll('img:not([alt])')
    images.forEach(img => {
      this.reportViolation({
        type: 'accessibility',
        severity: 'error',
        element: img,
        message: 'Image missing alt attribute',
        messageEs: 'Imagen sin atributo alt',
        messageEn: 'Image missing alt attribute',
        recommendation: 'Add descriptive alt text for screen readers',
        birhausPrinciple: 6 // Accessibility = dignity
      })
    })

    // Check for missing labels
    const inputs = element.querySelectorAll('input:not([aria-label]):not([aria-labelledby])')
    inputs.forEach(input => {
      const id = input.getAttribute('id')
      if (!id || !element.querySelector(`label[for="${id}"]`)) {
        this.reportViolation({
          type: 'accessibility',
          severity: 'error',
          element: input,
          message: 'Input field missing label',
          messageEs: 'Campo de entrada sin etiqueta',
          messageEn: 'Input field missing label',
          recommendation: 'Associate input with a label element or add aria-label',
          birhausPrinciple: 6 // Accessibility = dignity
        })
      }
    })
  }

  private reportViolation(violation: Omit<BirhausViolation, 'timestamp'>) {
    const fullViolation: BirhausViolation = {
      ...violation,
      timestamp: new Date()
    }
    
    this.violations.push(fullViolation)
    this.onViolation(fullViolation)
  }

  getViolations(): BirhausViolation[] {
    return [...this.violations]
  }

  clearViolations() {
    this.violations = []
  }
}

// eslint-disable-next-line max-lines-per-function -- Main provider component requires comprehensive configuration logic
export function BirhausProvider({
  language = 'es',
  config: userConfig,
  theme,
  analyticsEndpoint,
  scoreThresholds = { warning: 70, critical: 50 },
  devMode = typeof process !== 'undefined' && process.env.NODE_ENV === 'development',
  children,
  onViolation,
  onScoreChange,
  undoConfig = {},
  disableUndo = false,
  errorBoundary,
  disableErrorBoundary = false,
  subProviders = {}
}: BirhausProviderProps) {
  // Merge user config with environment defaults (memoized to prevent useEffect re-renders)
  const config = React.useMemo(() => ({
    ...BIRHAUS_ENV_CONFIG[process.env.NODE_ENV as keyof typeof BIRHAUS_ENV_CONFIG] || BIRHAUS_ENV_CONFIG.development,
    // v3.0 Radical Minimalist defaults
    v3: {
      glassEnabled: true,
      glassIntensity: 50,
      radicalBorders: true,
      generousSpacing: true,
      smoothAnimations: true,
      reducedMotion: false,
      floatingElements: true,
      magneticHover: true,
      enableNewComponents: true,
      performanceMode: false,
      ...userConfig?.v3
    },
    ...userConfig
  }), [userConfig])

  // SSR-safe client detection
  const [isClient, setIsClient] = React.useState(false)
  
  const [currentLanguage, setCurrentLanguage] = React.useState<'es' | 'en'>(language)
  const [currentTheme] = React.useState(theme) // setCurrentTheme not used in current implementation
  const [currentScore, setCurrentScore] = React.useState<BirhausScore | null>(null)
  const [violations, setViolations] = React.useState<BirhausViolation[]>([])
  const [currentConfig, setCurrentConfig] = React.useState<BirhausConfig>(config)

  // Set isClient to true after mount for SSR compatibility
  React.useEffect(() => {
    setIsClient(true)
  }, [])
  
  // Sub-providers state with defaults
  const [currentSubProviders, setCurrentSubProviders] = React.useState<BirhausSubProvidersConfig>(() => ({
    // Default locale configuration for Paraguay
    locale: {
      current: 'es-PY',
      fallback: 'es-ES',
      dateFormat: 'DD/MM/YYYY',
      numberFormat: 'es-PY',
      currencyCode: 'PYG',
      ...subProviders.locale
    },
    // Default compliance for financial systems
    compliance: {
      standards: ['SEPRELAD'],
      auditTrail: true,
      dataRetentionDays: 2555, // 7 years in days
      encryptionRequired: true,
      ...subProviders.compliance
    },
    // Default theme for financial industry with v3 support
    theme: {
      industry: 'financial',
      cssVariables: {},
      darkMode: false,
      v3: {
        useV3Tokens: true,
        glassTheme: 'medium',
        borderRadiusScale: 'lg',
        spacingScale: 2,
        animationTiming: 'base',
        typographyScale: 'base',
        ...subProviders.theme?.v3
      },
      ...subProviders.theme
    },
    // Default loading configuration
    loading: {
      globalLoading: false,
      defaultTimeout: 30000, // 30 seconds
      showSkeletons: true,
      loadingMessages: {
        es: 'Cargando...',
        en: 'Loading...'
      },
      ...subProviders.loading
    }
  }))
  
  const validatorRef = React.useRef<BirhausRuntimeValidator | null>(null)
  const analyticsQueueRef = React.useRef<Record<string, unknown>[]>([])
  const handleViolationRef = React.useRef<((violation: BirhausViolation) => void) | null>(null)

  // Translation function
  const t = React.useCallback((key: string, fallback?: string) => {
    // In a real implementation, this would use a translation system
    // For now, we'll return the key or fallback
    return fallback || key
  }, [])

  // Handle violation reports
  const handleViolation = React.useCallback((violation: BirhausViolation) => {
    setViolations(prev => [...prev, violation])
    
    // Log in development
    if (devMode && config.detailedLogging) {
      console.group(`🧠 BIRHAUS ${violation.severity.toUpperCase()}: ${violation.type}`)
      console.log('Message:', currentLanguage === 'es' ? violation.messageEs : violation.messageEn)
      console.log('Recommendation:', violation.recommendation)
      console.log('BIRHAUS Principle:', violation.birhausPrinciple)
      console.log('Element:', violation.element)
      console.groupEnd()
    }

    // Throw in strict mode during development
    if (devMode && config.strictMode && violation.severity === 'error') {
      throw new Error(`BIRHAUS Violation: ${violation.messageEn}`)
    }

    // Queue for analytics
    if (config.analyticsEnabled && analyticsEndpoint) {
      analyticsQueueRef.current.push({
        type: 'violation',
        data: violation,
        timestamp: new Date().toISOString()
      })
    }

    // Call user handler
    onViolation?.(violation)
  }, [devMode, config.strictMode, config.detailedLogging, config.analyticsEnabled, currentLanguage, analyticsEndpoint, onViolation])

  // Update ref when handleViolation changes
  React.useEffect(() => {
    handleViolationRef.current = handleViolation
  }, [handleViolation])

  // Report violation function
  const reportViolation = React.useCallback((violation: Omit<BirhausViolation, 'timestamp'>) => {
    handleViolation({
      ...violation,
      timestamp: new Date()
    })
  }, [handleViolation])

  // Update configuration
  const updateConfig = React.useCallback((newConfig: Partial<BirhausConfig>) => {
    setCurrentConfig(prev => ({ ...prev, ...newConfig }))
  }, [])

  // Update sub-providers configuration
  const updateSubProviders = React.useCallback((newSubProviders: Partial<BirhausSubProvidersConfig>) => {
    setCurrentSubProviders(prev => ({
      ...prev,
      ...newSubProviders,
      // Deep merge each sub-provider to preserve defaults
      locale: newSubProviders.locale ? { ...prev.locale, ...newSubProviders.locale } : prev.locale,
      compliance: newSubProviders.compliance ? { ...prev.compliance, ...newSubProviders.compliance } : prev.compliance,
      theme: newSubProviders.theme ? { ...prev.theme, ...newSubProviders.theme } : prev.theme,
      loading: newSubProviders.loading ? { ...prev.loading, ...newSubProviders.loading } : prev.loading
    }))
  }, [])

  // Recalculate score
  const recalculateScore = React.useCallback(async () => {
    // This would trigger the BirhausScoreDashboard calculation
    // For now, we'll create a mock score based on violations
    const cognitiveViolations = violations.filter(v => v.type === 'cognitive').length
    const accessibilityViolations = violations.filter(v => v.type === 'accessibility').length
    const spanishViolations = violations.filter(v => v.type === 'spanish').length
    const performanceViolations = violations.filter(v => v.type === 'performance').length

    const score: BirhausScore = {
      total: Math.max(0, 100 - (cognitiveViolations * 5) - (accessibilityViolations * 3) - (spanishViolations * 2) - (performanceViolations * 5)),
      performance: Math.max(0, 15 - performanceViolations * 5),
      accessibility: Math.max(0, 30 - accessibilityViolations * 3),
      cognitiveLoad: Math.max(0, 40 - cognitiveViolations * 5),
      spanishCoverage: Math.max(0, 15 - spanishViolations * 2),
      violations: violations.map(v => currentLanguage === 'es' ? v.messageEs : v.messageEn),
      recommendations: violations.map(v => v.recommendation),
      timestamp: new Date()
    }

    setCurrentScore(score)
    onScoreChange?.(score)
  }, [currentLanguage, onScoreChange])

  // Direct score calculation when violations change (avoiding infinite loop)
  React.useEffect(() => {
    const cognitiveViolations = violations.filter(v => v.type === 'cognitive').length
    const accessibilityViolations = violations.filter(v => v.type === 'accessibility').length
    const spanishViolations = violations.filter(v => v.type === 'spanish').length
    const performanceViolations = violations.filter(v => v.type === 'performance').length

    const score: BirhausScore = {
      total: Math.max(0, 100 - (cognitiveViolations * 5) - (accessibilityViolations * 3) - (spanishViolations * 2) - (performanceViolations * 5)),
      performance: Math.max(0, 15 - performanceViolations * 5),
      accessibility: Math.max(0, 30 - accessibilityViolations * 3),
      cognitiveLoad: Math.max(0, 40 - cognitiveViolations * 5),
      spanishCoverage: Math.max(0, 15 - spanishViolations * 2),
      violations: violations.map(v => currentLanguage === 'es' ? v.messageEs : v.messageEn),
      recommendations: violations.map(v => v.recommendation),
      timestamp: new Date()
    }

    setCurrentScore(score)
    onScoreChange?.(score)
  }, [violations, currentLanguage, onScoreChange])

  // Initialize runtime validator with stable callback
  React.useEffect(() => {
    if (typeof window === 'undefined') return

    if (config.cognitiveLoadTracking && !validatorRef.current) {
      validatorRef.current = new BirhausRuntimeValidator(config, (violation) => {
        handleViolationRef.current?.(violation)
      })
      validatorRef.current.start()
    }

    return () => {
      if (validatorRef.current) {
        validatorRef.current.stop()
        validatorRef.current = null
      }
    }
  }, [config])

  // Send analytics data periodically
  React.useEffect(() => {
    if (!config.analyticsEnabled || !analyticsEndpoint) return

    const sendAnalytics = async () => {
      if (analyticsQueueRef.current.length === 0) return

      try {
        await fetch(analyticsEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            events: analyticsQueueRef.current,
            sessionId: crypto.randomUUID(),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'SSR',
            timestamp: new Date().toISOString()
          })
        })
        
        analyticsQueueRef.current = []
      } catch (error) {
        console.warn('Failed to send BIRHAUS analytics:', error)
      }
    }

    const interval = setInterval(sendAnalytics, 30000) // Send every 30 seconds
    return () => clearInterval(interval)
  }, [config.analyticsEnabled, analyticsEndpoint])

  // Recalculate score when violations change
  React.useEffect(() => {
    recalculateScore()
  }, [recalculateScore])

  // SSR detection
  const isSSR = typeof window === 'undefined'

  // v3 Design System Utilities - Memoized for performance
  const v3Utils = React.useMemo(() => ({
    getGlassEffect: (intensity = 50) => {
      const effectIntensity = currentConfig.v3?.glassIntensity || intensity
      if (effectIntensity < 30) return glassV3.effects.light
      if (effectIntensity > 70) return glassV3.effects.heavy
      return glassV3.effects.medium
    },
    
    getRadicalBorders: () => borderRadiusV3,
    
    getGenerousSpacing: () => spacingV3,
    
    isV3Enabled: () => currentConfig.v3?.enableNewComponents ?? false,
    
    getAnimationPrefs: () => {
      const prefersReducedMotion = isClient && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const useReducedMotion = currentConfig.v3?.reducedMotion ?? prefersReducedMotion
      
      return {
        duration: useReducedMotion ? '0ms' : animationsV3.duration.base,
        easing: animationsV3.easing.smooth,
        reducedMotion: useReducedMotion
      }
    },
    
    generateV3CSSVars: () => {
      const vars: Record<string, string> = {}
      
      // Glass morphism variables
      if (currentConfig.v3?.glassEnabled) {
        const glassEffect = v3Utils.getGlassEffect()
        vars['--birhaus-glass-bg'] = glassEffect.background
        vars['--birhaus-glass-backdrop'] = glassEffect.backdropFilter
        vars['--birhaus-glass-border'] = glassEffect.border
        vars['--birhaus-glass-shadow'] = glassEffect.boxShadow
      }
      
      // Radical border radius variables
      if (currentConfig.v3?.radicalBorders) {
        Object.entries(borderRadiusV3).forEach(([key, value]) => {
          vars[`--birhaus-radius-${key}`] = value
        })
      }
      
      // Generous spacing variables
      if (currentConfig.v3?.generousSpacing) {
        const multiplier = currentSubProviders.theme?.v3?.spacingScale || 2
        Object.entries(spacingV3).forEach(([key, value]) => {
          const numericValue = parseInt(value.replace('px', ''))
          vars[`--birhaus-spacing-${key}`] = `${numericValue * multiplier}px`
        })
      }
      
      // Animation timing variables
      const animPrefs = v3Utils.getAnimationPrefs()
      vars['--birhaus-animation-duration'] = animPrefs.duration
      vars['--birhaus-animation-easing'] = animPrefs.easing
      
      return vars
    },
    
    hasAdvancedSupport: () => {
      if (!isClient) return { backdropFilter: false, transforms: false, animations: false }
      
      const testEl = document.createElement('div')
      return {
        backdropFilter: 'backdropFilter' in testEl.style || 'webkitBackdropFilter' in testEl.style,
        transforms: 'transform' in testEl.style,
        animations: 'animation' in testEl.style
      }
    }
  }), [currentConfig, currentSubProviders, isClient])

  const contextValue: BirhausContextType = {
    config: currentConfig,
    language: currentLanguage,
    theme: currentTheme || {},
    score: currentScore,
    violations,
    t,
    reportViolation,
    updateConfig,
    setLanguage: setCurrentLanguage,
    recalculateScore,
    subProviders: currentSubProviders,
    updateSubProviders,
    isClient,
    v3: v3Utils
  }

  // Wrap children with UndoProvider unless disabled
  const wrappedChildren = !disableUndo ? (
    <UndoProvider
      defaultTimeoutMs={undoConfig.defaultTimeoutMs}
      maxActions={undoConfig.maxActions}
    >
      {children}
    </UndoProvider>
  ) : children

  const providerContent = (
    <BirhausContext.Provider value={contextValue}>
      <div 
        className="birhaus-provider"
        {...(isClient ? {
          'data-birhaus-language': currentLanguage,
          'data-birhaus-score': currentScore?.total,
          'data-birhaus-violations': violations.length,
          'data-birhaus-ssr': isSSR,
          'data-birhaus-v3-enabled': v3Utils.isV3Enabled(),
          'data-birhaus-v3-glass': currentConfig.v3?.glassEnabled ?? false,
          'data-birhaus-v3-radical-borders': currentConfig.v3?.radicalBorders ?? false,
          'data-birhaus-v3-generous-spacing': currentConfig.v3?.generousSpacing ?? false,
          'data-birhaus-v3-reduced-motion': v3Utils.getAnimationPrefs().reducedMotion,
          'data-birhaus-theme-industry': currentSubProviders.theme?.industry,
          'data-birhaus-glass-support': v3Utils.hasAdvancedSupport().backdropFilter
        } : {})}
        style={{
          ...(currentTheme?.cssVariables && typeof currentTheme.cssVariables === 'object' 
            ? currentTheme.cssVariables as React.CSSProperties 
            : {}),
          // Merge v3 CSS variables
          ...v3Utils.generateV3CSSVars()
        } as React.CSSProperties}
      >
        {wrappedChildren}
      </div>
    </BirhausContext.Provider>
  )

  // Wrap with ErrorBoundary unless disabled
  if (disableErrorBoundary) {
    return providerContent
  }

  return (
    <BirhausErrorBoundary
      language={currentLanguage}
      fallback={errorBoundary?.fallback}
      onError={errorBoundary?.onError}
    >
      {providerContent}
    </BirhausErrorBoundary>
  )
}

export function useBirhaus() {
  const context = React.useContext(BirhausContext)
  if (!context) {
    throw new Error('useBirhaus must be used within BirhausProvider')
  }
  return context
}

/**
 * Hook to access v3.0 Design System utilities and tokens
 * Provides access to glass morphism, radical minimalism, and animation utilities
 */
export function useBirhausV3() {
  const { v3, config, isClient } = useBirhaus()
  return {
    ...v3,
    // Additional v3 utilities
    isV3Ready: () => isClient && v3.isV3Enabled(),
    getV3ClassNames: (component: string) => {
      const classes = ['birhaus-v3']
      
      if (config.v3?.glassEnabled) classes.push('birhaus-v3-glass')
      if (config.v3?.radicalBorders) classes.push('birhaus-v3-radical')
      if (config.v3?.generousSpacing) classes.push('birhaus-v3-generous')
      if (config.v3?.smoothAnimations) classes.push('birhaus-v3-animated')
      if (config.v3?.floatingElements) classes.push('birhaus-v3-floating')
      if (config.v3?.magneticHover) classes.push('birhaus-v3-magnetic')
      
      classes.push(`birhaus-${component}-v3`)
      
      return classes.join(' ')
    },
    // Style object generator for inline styles
    getV3Styles: (baseStyles: React.CSSProperties = {}) => ({
      ...baseStyles,
      ...(config.v3?.radicalBorders && {
        borderRadius: borderRadiusV3.lg
      }),
      ...(config.v3?.glassEnabled && {
        ...v3.getGlassEffect()
      }),
      ...(config.v3?.smoothAnimations && !v3.getAnimationPrefs().reducedMotion && {
        transition: `all ${v3.getAnimationPrefs().duration} ${v3.getAnimationPrefs().easing}`,
        willChange: 'transform, opacity'
      })
    }),
    // Migration helpers
    shouldUseV3Component: (componentName: string) => {
      return v3.isV3Enabled() && config.v3?.enableNewComponents
    }
  }
}

/**
 * Hook to access locale configuration and utilities
 */
export function useBirhausLocale() {
  const { subProviders, updateSubProviders } = useBirhaus()
  return {
    config: subProviders.locale,
    updateLocale: (newConfig: Partial<BirhausLocaleConfig>) => 
      updateSubProviders({ locale: { ...subProviders.locale, ...newConfig } as BirhausLocaleConfig }),
    // Utility functions
    formatDate: (date: Date) => {
      if (!subProviders.locale?.current) return date.toLocaleDateString()
      return date.toLocaleDateString(subProviders.locale.current)
    },
    formatNumber: (num: number) => {
      if (!subProviders.locale?.current) return num.toString()
      return num.toLocaleString(subProviders.locale.current)
    },
    formatCurrency: (amount: number) => {
      if (!subProviders.locale?.current || !subProviders.locale?.currencyCode) {
        return `$${amount}`
      }
      return new Intl.NumberFormat(subProviders.locale.current, {
        style: 'currency',
        currency: subProviders.locale.currencyCode
      }).format(amount)
    }
  }
}

/**
 * Hook to access compliance configuration
 */
export function useBirhausCompliance() {
  const { subProviders, updateSubProviders } = useBirhaus()
  return {
    config: subProviders.compliance,
    updateCompliance: (newConfig: Partial<BirhausComplianceConfig>) => 
      updateSubProviders({ compliance: { ...subProviders.compliance, ...newConfig } as BirhausComplianceConfig }),
    // Utility functions
    isComplianceRequired: (standard: ComplianceStandard) => 
      subProviders.compliance?.standards.includes(standard) ?? false,
    shouldAudit: () => subProviders.compliance?.auditTrail ?? false,
    getDataRetentionDays: () => subProviders.compliance?.dataRetentionDays ?? 0
  }
}

/**
 * Hook to access theme configuration
 */
export function useBirhausTheme() {
  const { subProviders, updateSubProviders } = useBirhaus()
  return {
    config: subProviders.theme,
    updateTheme: (newConfig: Partial<BirhausThemeConfig>) => 
      updateSubProviders({ theme: { ...subProviders.theme, ...newConfig } as BirhausThemeConfig }),
    // Utility functions
    isDarkMode: () => subProviders.theme?.darkMode ?? false,
    getIndustryTheme: () => subProviders.theme?.industry ?? 'financial',
    getCSSVariable: (name: string) => subProviders.theme?.cssVariables?.[name] ?? ''
  }
}

/**
 * Hook to access loading configuration
 */
export function useBirhausLoading() {
  const { subProviders, updateSubProviders } = useBirhaus()
  return {
    config: subProviders.loading,
    updateLoading: (newConfig: Partial<BirhausLoadingConfig>) => 
      updateSubProviders({ loading: { ...subProviders.loading, ...newConfig } as BirhausLoadingConfig }),
    // Utility functions
    isGlobalLoading: () => subProviders.loading?.globalLoading ?? false,
    getLoadingMessage: (lang: 'es' | 'en' = 'es') => 
      subProviders.loading?.loadingMessages?.[lang] ?? 'Cargando...',
    shouldShowSkeletons: () => subProviders.loading?.showSkeletons ?? true
  }
}

export default BirhausProvider
export type { BirhausConfig, BirhausViolation }
